import { Grid } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import moment, { Moment } from "moment";
import React, { useMemo, useState } from "react";
import { KboFlowDebugService } from "services/KboFlowDebugService";
import { UserForSharing } from "services/UserService";
import { useStore } from "utils/useStore";
import {
    DEFAULT_KBO_FLOW_WORKFLOW_INPUT,
    isKboFlowWorkflowInputValid,
    KboFlowWorkflowInputFields,
} from "./KboFlowWorkflowInputFields";

type Props = {
    open: boolean;
    onClose: () => void;
    service: KboFlowDebugService;
    users: UserForSharing[];
    onScheduled?: () => void;
};

const DEFAULT_WORKFLOW_ID = "KboFlowWorkflow:v2";

export function ScheduleWorkflowDialog({
    open,
    onClose,
    service,
    users,
    onScheduled,
}: Props) {
    const messageStore = useStore(MessageStore);
    const [startDate, setStartDate] = useState<Moment>(() =>
        moment().subtract(30, "days"),
    );
    const [endDate, setEndDate] = useState<Moment>(() => moment());
    const [workflowId, setWorkflowId] = useState(DEFAULT_WORKFLOW_ID);
    const [workflowInput, setWorkflowInput] = useState({
        ...DEFAULT_KBO_FLOW_WORKFLOW_INPUT,
    });
    const [subjectUserId, setSubjectUserId] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);

    const userOptions = useMemo(
        () =>
            users.map((u) => ({
                userId: u.userId,
                label: `${u.firstName} ${u.lastName} (${u.email})`.trim(),
            })),
        [users],
    );

    const valid =
        isKboFlowWorkflowInputValid(workflowInput) &&
        workflowId.trim().length > 0 &&
        startDate.isValid() &&
        endDate.isValid() &&
        !endDate.isBefore(startDate);

    const handleSubmit = async () => {
        if (!valid) return;
        try {
            setSubmitting(true);
            await service.startWorkflow({
                workflowId: workflowId.trim(),
                startDateUtc: startDate.clone().startOf("day").toISOString(),
                endDateUtc: endDate.clone().endOf("day").toISOString(),
                targetQuestionCount: workflowInput.targetQuestionCount,
                maxQuestionsToPursue:
                    workflowInput.maxQuestionsToPursue,
                subjectUserId: subjectUserId || undefined,
                ignoreHistory: workflowInput.ignoreHistory,
            });
            messageStore.logMessage("KboFlow run scheduled", "success");
            onClose();
            onScheduled?.();
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "Failed to schedule KboFlow run",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AcxDialog
            isOpen={open}
            onClose={onClose}
            title="Schedule KboFlow Run"
            subTitle="Queue a new KboFlow workflow for this organization."
            maxWidth="sm"
            fullWidth
            buttonsDisabled={submitting}
            dialogContentChildren={
                <Grid container spacing={2} direction="column" sx={{ pt: 1 }}>
                    <Grid item>
                        <AcxDateRangeInput
                            labelText="Date Range"
                            required
                            defaultStartDate={startDate}
                            defaultEndDate={endDate}
                            onSelect={(selectedStart, selectedEnd) => {
                                setStartDate(selectedStart);
                                setEndDate(selectedEnd);
                            }}
                            maxWidth="100%"
                        />
                    </Grid>
                    <Grid item>
                        <AcxSelectSingle
                            id="kfd-subject-user"
                            inputLabel="Subject User (optional)"
                            placeholder="Default: current user"
                            options={userOptions}
                            valueField="userId"
                            labelField="label"
                            isClearable
                            fullWidth
                            defaultValue={userOptions.find(
                                (o) => o.userId === subjectUserId,
                            )}
                            onChange={(option?: {
                                userId: string;
                                label: string;
                            }) => setSubjectUserId(option?.userId)}
                        />
                    </Grid>
                    <KboFlowWorkflowInputFields
                        idPrefix="kfd"
                        value={workflowInput}
                        onChange={setWorkflowInput}
                        advancedChildren={
                            <Grid item>
                                <AcxMainTextField
                                    id="kfd-workflow-id"
                                    labelText="Workflow ID"
                                    value={workflowId}
                                    onChange={(event) =>
                                        setWorkflowId(event.target.value)
                                    }
                                    required
                                />
                            </Grid>
                        }
                    />
                </Grid>
            }
        >
            <AcxButton
                color="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!valid || submitting}
            >
                Schedule
            </AcxButton>
        </AcxDialog>
    );
}

export default ScheduleWorkflowDialog;
